import {
  loadSessionStorageState,
  removeSessionStorageState,
  saveSessionStorageState,
} from '@/utils/sessionStorage'

export const CHUNK_RELOAD_KEY = 'agilus.chunkReloadAt'

export const CHUNK_RELOAD_COOLDOWN_MS = 30_000

// How long a page load must survive before it counts as a recovery.
export const CHUNK_RELOAD_SETTLE_MS = 10_000

interface ChunkReloadDeps {
  now: () => number
  loadState: (key: string) => unknown
  saveState: (key: string, value: unknown) => void
  removeState: (key: string) => void
  reload: () => void
}

export function createChunkReloader({
  now = Date.now,
  loadState = loadSessionStorageState,
  saveState = saveSessionStorageState,
  removeState = removeSessionStorageState,
  reload = () => window.location.reload(),
}: Partial<ChunkReloadDeps> = {}) {
  let triggered = false
  // Set by every request, including suppressed ones: it marks this page load as
  // one that hit a chunk failure, which is what settleChunkReload checks
  let requested = false

  // true  — a reload is in flight; render the "updating" state
  // false — suppressed; render the manual reload button
  function requestChunkReload(): boolean {
    if (triggered) return true
    if (typeof window === 'undefined') return false

    requested = true

    const at = now()
    const previous = loadState(CHUNK_RELOAD_KEY) as { at?: unknown } | undefined
    if (
      previous &&
      typeof previous.at === 'number' &&
      at - previous.at < CHUNK_RELOAD_COOLDOWN_MS
    ) {
      return false
    }

    saveState(CHUNK_RELOAD_KEY, { at })
    // Read back before navigating — no durable marker, no reload
    const persisted = loadState(CHUNK_RELOAD_KEY) as
      | { at?: unknown }
      | undefined
    if (!persisted || persisted.at !== at) return false

    triggered = true
    reload()
    return true
  }

  function settleChunkReload(): void {
    if (requested) return
    removeState(CHUNK_RELOAD_KEY)
  }

  return { requestChunkReload, settleChunkReload }
}

// Shared instance so the once-per-page-load flags hold across both boundaries
export const { requestChunkReload, settleChunkReload } = createChunkReloader()
